import React, { useEffect, useState, useRef } from 'react';

interface CustomYouTubePlayerProps {
  videoId: string;
  title?: string;
}

// This global variable is needed to interact with YouTube IFrame API
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const CustomYouTubePlayer: React.FC<CustomYouTubePlayerProps> = ({ videoId, title = 'Video' }) => {
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [progressPercent, setProgressPercent] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  
  const playerRef = useRef<any>(null);
  const playerElementId = `player-${videoId}`;
  const progressInterval = useRef<number | null>(null);

  // Load YouTube API script
  useEffect(() => {
    // Only load the API once
    if (!document.getElementById('youtube-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    // Initialize player when API is ready
    const initializePlayer = () => {
      if (window.YT && window.YT.Player && document.getElementById(playerElementId)) {
        playerRef.current = new window.YT.Player(playerElementId, {
          videoId: videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            fs: 0,
            iv_load_policy: 3,
            disablekb: 1,
            enablejsapi: 1
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange
          }
        });
      } else {
        // Try again if the element or YT is not available yet
        setTimeout(initializePlayer, 100);
      }
    };

    // Setup for YouTube API ready callback
    const prevReadyFunction = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prevReadyFunction) prevReadyFunction();
      initializePlayer();
    };

    // If YT is already loaded, initialize directly
    if (window.YT && window.YT.Player) {
      initializePlayer();
    }

    return () => {
      // Cleanup interval on unmount
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      // Avoid memory leaks by cleaning up the player
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, playerElementId]);

  // Player event handlers
  const onPlayerReady = (event: any) => {
    setPlayerReady(true);
    setDuration(event.target.getDuration());
    setVolume(event.target.getVolume());
    setPlaybackSpeed(event.target.getPlaybackRate());
    
    // Start updating progress
    progressInterval.current = window.setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        const currentTime = playerRef.current.getCurrentTime();
        const duration = playerRef.current.getDuration();
        setCurrentTime(currentTime);
        setProgressPercent((currentTime / duration) * 100);
      }
    }, 200);
  };

  const onPlayerStateChange = (event: any) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  // Player control functions
  const togglePlayPause = () => {
    if (!playerReady || !playerRef.current) return;
    
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const toggleMute = () => {
    if (!playerReady || !playerRef.current) return;
    
    if (isMuted) {
      playerRef.current.unMute();
      setVolume(playerRef.current.getVolume());
    } else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  };

  const changeVolume = (newVolume: number) => {
    if (!playerReady || !playerRef.current) return;
    
    playerRef.current.setVolume(newVolume);
    setVolume(newVolume);
    
    if (newVolume === 0) {
      playerRef.current.mute();
      setIsMuted(true);
    } else if (isMuted) {
      playerRef.current.unMute();
      setIsMuted(false);
    }
  };

  const seekVideo = (percent: number) => {
    if (!playerReady || !playerRef.current) return;
    
    const newTime = (percent / 100) * duration;
    playerRef.current.seekTo(newTime, true);
  };

  const cyclePlaybackSpeed = () => {
    if (!playerReady || !playerRef.current) return;
    
    // Cycle through speeds: 1 -> 1.25 -> 1.5 -> 2 -> 1
    const speeds = [1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    
    playerRef.current.setPlaybackRate(newSpeed);
    setPlaybackSpeed(newSpeed);
  };

  const toggleFullscreen = () => {
    const container = document.getElementById(`video-container-${videoId}`);
    if (!container) return;
    
    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        (container as any).webkitRequestFullscreen();
      } else if ((container as any).msRequestFullscreen) {
        (container as any).msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  // Format time (seconds) to MM:SS format
  const formatTime = (timeInSeconds: number) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="video-wrapper-container">
      <div id={`video-container-${videoId}`} className="relative w-full rounded-t-lg overflow-hidden aspect-video bg-black">
        <div id={playerElementId} className="w-full h-full"></div>
        <div 
          className="absolute inset-0 cursor-pointer z-10" 
          onClick={togglePlayPause}
        ></div>
      </div>
      
      <div className="flex justify-between items-center bg-gradient-to-r from-gray-800 to-gray-900 py-3 px-4 rounded-b-lg w-full">
        <div className="flex items-center gap-3">
          <button 
            onClick={togglePlayPause} 
            className="bg-transparent border-none text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-all"
          >
            {isPlaying ? (
              <span className="text-xl">⏸️</span>
            ) : (
              <span className="text-xl">▶</span>
            )}
          </button>
          
          <div className="text-sm text-gray-300 min-w-[75px]">
            <span id={`current-time-${videoId}`}>{formatTime(currentTime)}</span> / <span id={`total-time-${videoId}`}>{formatTime(duration)}</span>
          </div>
        </div>
        
        <div className="flex-grow mx-4">
          <input 
            type="range"
            className="w-full h-1.5 bg-gray-600 rounded-full appearance-none cursor-pointer"
            style={{
              backgroundImage: `linear-gradient(to right, #ff0000 ${progressPercent}%, #666 ${progressPercent}%)`
            }}
            min="0"
            max="100"
            step="0.1"
            value={progressPercent}
            onChange={(e) => seekVideo(parseFloat(e.target.value))}
          />
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 w-[90px]">
            <button 
              onClick={toggleMute}
              className="bg-transparent border-none text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-all"
            >
              {isMuted ? <span className="text-xl">🔇</span> : <span className="text-xl">🔊</span>}
            </button>
            
            <input 
              type="range"
              className="w-[60px] h-1.5 bg-gray-600 rounded-full appearance-none cursor-pointer"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={(e) => changeVolume(parseInt(e.target.value))}
            />
          </div>
          
          <button 
            onClick={cyclePlaybackSpeed}
            className="bg-transparent border-none text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-all"
            title="Cycle playback speed"
          >
            <span className="text-sm font-bold">{playbackSpeed}x</span>
          </button>
          
          <button 
            onClick={toggleFullscreen}
            className="bg-transparent border-none text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/10 transition-all"
          >
            <span className="text-xl">⛶</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomYouTubePlayer; 